import { ISelectorProps } from 'telia-front-react';
import { InternetPrice, Prices } from './MeluCalculator';

export const defaultInternetDeviceCount = 1;
export const defaultCallsDeviceCount = 1;
export const defaultInfinteCallsProductId = '11307';
export const defaultInternetDeviceProductId = '7745';
const defaultInfiniteCallsDevicePrice = '5.991';
const defaultInternetDevicePrice = '1.513';

interface CalculationProps {
  internetDeviceCount: number;
  callsDeviceCount: number;
  internetDataAmount: number;
  prices: Prices;
}

const defaultDataPrices: KeyValue<InternetPrice> = {
  '3': {
    price: '6',
    showAsterisk: false,
    productId: '13095',
    roaming: '3',
    singleDevice: { price: '6.991', roaming: '3' }
  },
  '8': {
    price: '15',
    pricesPerUser: [12.993, 15.994, 15.994, 15.994, 15.994],
    showAsterisk: false,
    productId: '13107',
    roaming: '8',
    singleDevice: { price: '13.0', roaming: '8' }
  },
  '16': {
    price: '20',
    pricesPerUser: [19.996, 23.997, 23.997, 23.997, 23.997],
    showAsterisk: false,
    productId: '13108',
    roaming: '16',
    singleDevice: { price: '19.996', roaming: '16' }
  },
  '50': {
    price: '35',
    pricesPerUser: [26.996, 38.991, 38.991, 38.991, 38.991],
    showAsterisk: false,
    productId: '13110',
    roaming: '50',
    singleDevice: { price: '26.999', roaming: '36' }
  },
  '100': {
    price: '50',
    pricesPerUser: [34.99, 54.998, 54.998, 54.998, 54.998],
    showAsterisk: false,
    productId: '13111',
    roaming: '70',
    singleDevice: { price: '34.990', roaming: '45' }
  },
  '101': {
    price: '50',
    showAsterisk: true,
    productId: '13111',
    roaming: '42',
    pricesPerUser: [34.99, 54.998, 54.998, 54.998, 54.998]
  }
};

const customLabels = (translate: (key: string) => string): Map<string, string> => {
  return new Map<string, string>([
    ['100', translate('meluCalculator.chooseParametersCard.unlimited')],
    ['101', translate('meluCalculator.chooseParametersCard.unlimitedWithNetflix')]
  ]);
};

export const getInfiniteCallsDevicePrice = (prices: Prices): string => {
  const infiniteCallsDevicePrice = prices && prices.infiniteCallsDevicePrice;
  return infiniteCallsDevicePrice || defaultInfiniteCallsDevicePrice;
};

export const getInternetDevicePrice = (prices: Prices): string => {
  const internetDevicePrice = prices && prices.internetDevicePrice;
  return internetDevicePrice || defaultInternetDevicePrice;
};

export const getDataPrices = (prices: Prices): KeyValue<InternetPrice> => {
  const internetDataPrices = prices?.internetDataPrices;
  return internetDataPrices || defaultDataPrices;
};

const numericSort = (a: number, b: number) => a - b;

export const resolveInternetDataKeyFromIndex = (index: number, internetDataPrices: KeyValue<InternetPrice> = defaultDataPrices): number => {
  return +Object.keys(internetDataPrices)[index];
};

export const resolveInternetDataValueFromIndex = (index: number, internetDataPrices: KeyValue<InternetPrice> = defaultDataPrices): InternetPrice => {
  return Object.values(internetDataPrices)[index];
};

const defaultInternetDataAmount = (prices: Prices) => resolveInternetDataKeyFromIndex(3, getDataPrices(prices));

export const getFirstInternetDataWithAsterisk = (prices: Prices): number => {
  const dataPrices: KeyValue<InternetPrice> = getDataPrices(prices);
  const sortedKeys: number[] = Object.keys(dataPrices)
    .map((price) => +price)
    .sort(numericSort);
  const keysWithAsterisk = sortedKeys.filter((key: number) => dataPrices[`${key}`].showAsterisk);
  return keysWithAsterisk.length ? keysWithAsterisk[0] : defaultInternetDataAmount(prices);
};

export const getFirstRoamingDataAmountWithAsterisk = (prices: Prices): string => {
  const dataPrices: KeyValue<InternetPrice> = getDataPrices(prices);
  return Object.values(dataPrices).filter((price) => price.showAsterisk)[0]?.roaming || '29';
};

export const resolveInternetDataIndexFromKey = (index: number, internetDataPrices: KeyValue<InternetPrice> = defaultDataPrices): number => {
  return Object.keys(internetDataPrices).indexOf(`${index}`);
};

export const resolveInternetDataProductIdFromKey = (key: number, prices: Prices): string => {
  const internetDataPrices = getDataPrices(prices);
  return internetDataPrices[`${key}`].productId;
};

export const internetSelectorProps = (translate: (key: string) => string): ISelectorProps => {
  return {
    id: 'internet-slider',
    label: translate('meluCalculator.chooseParametersCard.devices'),
    hideLabel: true,
    items: [
      { id: '0', name: 'internetSliderDevicesCount', label: '0', value: '0', type: 'employee' },
      { id: '1', name: 'internetSliderDevicesCount', label: '1', value: '1', type: 'employee' },
      { id: '2', name: 'internetSliderDevicesCount', label: '2', value: '2', type: 'employee' },
      { id: '3', name: 'internetSliderDevicesCount', label: '3', value: '3', type: 'employee' },
      { id: '4', name: 'internetSliderDevicesCount', label: '4', value: '4', type: 'employee' },
      { id: '5', name: 'internetSliderDevicesCount', label: '5', value: '5', type: 'employee' }
    ]
  };
};

export const callsSelectorProps = (disabledCallsPips: boolean[], translate: (key: string) => string): ISelectorProps => {
  return {
    id: 'calls-slider',
    label: translate('meluCalculator.chooseParametersCard.devices'),
    hideLabel: true,
    items: [
      { id: '0', label: '0', name: 'callsSliderDevicesCount', value: '0', disabled: disabledCallsPips[0], type: 'employee' },
      { id: '1', label: '1', name: 'callsSliderDevicesCount', value: '1', disabled: disabledCallsPips[1], type: 'employee' },
      { id: '2', label: '2', name: 'callsSliderDevicesCount', value: '2', disabled: disabledCallsPips[2], type: 'employee' },
      { id: '3', label: '3', name: 'callsSliderDevicesCount', value: '3', disabled: disabledCallsPips[3], type: 'employee' },
      { id: '4', label: '4', name: 'callsSliderDevicesCount', value: '4', disabled: disabledCallsPips[4], type: 'employee' },
      { id: '5', label: '5', name: 'callsSliderDevicesCount', value: '5', disabled: disabledCallsPips[5], type: 'employee' }
    ]
  };
};

export const internetDataSelectorProps = (
  disabledDataPips: boolean[],
  translate: (key: string) => string,
  internetDataPrices: KeyValue<InternetPrice> = defaultDataPrices
): ISelectorProps => {
  const dataUnit = translate('meluCalculator.chooseParametersCard.dataUnit');
  return {
    id: 'internet-data-slider',
    label: translate('meluCalculator.chooseParametersCard.dataLabel'),
    hideLabel: true,
    items: Object.keys(internetDataPrices).map((key: string, index: number) => ({
      id: key === '100' ? '∞' : `${key} ${dataUnit}`,
      label: customLabels(translate).get(key) ? customLabels(translate).get(key) : `${key} ${dataUnit}`,
      name: 'internetDataSliderVolume',
      value: `${index}`,
      disabled: disabledDataPips[index],
      type: 'employee'
    })),
    size: 'large'
  };
};

export const resolveInternetDataPrice = (internetDeviceCount: number, dataAmount: number, prices: Prices) => {
  if (!internetDeviceCount) {
    return 0;
  }
  const internetDataPrices = getDataPrices(prices);

  if (
    !!internetDataPrices[`${dataAmount}`].pricesPerUser &&
    internetDataPrices[`${dataAmount}`].pricesPerUser.length === 5 &&
    !!internetDataPrices[`${dataAmount}`].pricesPerUser[internetDeviceCount - 1]
  ) {
    return internetDataPrices[`${dataAmount}`].pricesPerUser[internetDeviceCount - 1];
  }
  if (internetDeviceCount === 1 && !!internetDataPrices[`${dataAmount}`].singleDevice) {
    return +internetDataPrices[`${dataAmount}`].singleDevice.price;
  }
  return +internetDataPrices[`${dataAmount}`].price;
};

export const resolveCallsPrice = (internetDeviceCount: number, callsDeviceCount: number, prices: Prices) => {
  const devicesForOnlyInternet = internetDeviceCount - callsDeviceCount;
  if (internetDeviceCount === 0) {
    return +getInfiniteCallsDevicePrice(prices);
  } else if (internetDeviceCount === 1 && callsDeviceCount === 0) {
    return 0;
  } else {
    const infiniteCallsDevicesPrice = callsDeviceCount * +getInfiniteCallsDevicePrice(prices);
    const onlyInternetDevicesPrice = devicesForOnlyInternet * +getInternetDevicePrice(prices);
    return infiniteCallsDevicesPrice + onlyInternetDevicesPrice;
  }
};

export const resolveTotalPrice = (props: CalculationProps) => {
  return (
    resolveInternetDataPrice(props.internetDeviceCount, props.internetDataAmount, props.prices) +
    resolveCallsPrice(props.internetDeviceCount, props.callsDeviceCount, props.prices)
  );
};

export const resolveAverageDevicePrice = (props: CalculationProps) => {
  return resolveTotalPrice(props) / (props.internetDeviceCount || 1);
};

export const enableAllPips = () => [false, false, false, false, false, false];

export const disableAllPips = () => [true, true, true, true, true, true];

export const onlyOneDeviceEnabledPips = () => [true, false, true, true, true, true];

export const disablePipsLargerThan = (size: number) => enableAllPips().map((pip, index) => index > size);

export const disableSmallestPip = () => [true, false, false, false, false, false];
